/** Minimum scroll delta (px) before dismiss fires once armed. */
export const FLOATING_TOOLBAR_SCROLL_DISMISS_THRESHOLD = 3;

/** Ignore layout-induced scroll briefly after the toolbar appears. */
export const FLOATING_TOOLBAR_SCROLL_DISMISS_GRACE_MS = 300;

/** Layout settle scroll is typically small; intentional dismiss scroll is larger. */
export const FLOATING_TOOLBAR_INTENTIONAL_SCROLL_DELTA = 20;

export interface FloatingToolbarScrollSample {
  scrollTop: number;
  scrollLeft: number;
}

export interface FloatingToolbarScrollDismissState {
  /** When false, scroll deltas update baseline only (layout settle). */
  armed: boolean;
  lastScrollTop: number;
  lastScrollLeft: number;
}

export function createScrollDismissState(
  sample: FloatingToolbarScrollSample,
): FloatingToolbarScrollDismissState {
  return {
    armed: false,
    lastScrollTop: sample.scrollTop,
    lastScrollLeft: sample.scrollLeft,
  };
}

export function armScrollDismissState(
  state: FloatingToolbarScrollDismissState,
  sample: FloatingToolbarScrollSample,
): FloatingToolbarScrollDismissState {
  return {
    ...state,
    armed: true,
    lastScrollTop: sample.scrollTop,
    lastScrollLeft: sample.scrollLeft,
  };
}

/** Re-baseline after layout/geometry changes without dismissing. */
export function resyncScrollDismissBaseline(
  state: FloatingToolbarScrollDismissState,
  sample: FloatingToolbarScrollSample,
): FloatingToolbarScrollDismissState {
  return {
    ...state,
    lastScrollTop: sample.scrollTop,
    lastScrollLeft: sample.scrollLeft,
  };
}

/**
 * Returns whether the floating toolbar should dismiss for this scroll sample.
 * While unarmed, layout-induced scroll is absorbed into the baseline.
 */
export interface FloatingToolbarScrollDismissContext {
  shownAtMs: number;
  nowMs: number;
  graceMs?: number;
}

export function evaluateFloatingToolbarScrollDismiss(
  state: FloatingToolbarScrollDismissState,
  sample: FloatingToolbarScrollSample,
  context?: FloatingToolbarScrollDismissContext,
): { nextState: FloatingToolbarScrollDismissState; dismiss: boolean } {
  const graceMs = context?.graceMs ?? FLOATING_TOOLBAR_SCROLL_DISMISS_GRACE_MS;
  const deltaTop = Math.abs(sample.scrollTop - state.lastScrollTop);
  const deltaLeft = Math.abs(sample.scrollLeft - state.lastScrollLeft);
  const intentionalScroll =
    deltaTop >= FLOATING_TOOLBAR_INTENTIONAL_SCROLL_DELTA ||
    deltaLeft >= FLOATING_TOOLBAR_INTENTIONAL_SCROLL_DELTA;

  if (context && context.nowMs - context.shownAtMs < graceMs && !intentionalScroll) {
    return {
      nextState: resyncScrollDismissBaseline(state, sample),
      dismiss: false,
    };
  }

  if (!state.armed) {
    return {
      nextState: {
        ...state,
        lastScrollTop: sample.scrollTop,
        lastScrollLeft: sample.scrollLeft,
      },
      dismiss: false,
    };
  }

  const nextState: FloatingToolbarScrollDismissState = {
    ...state,
    lastScrollTop: sample.scrollTop,
    lastScrollLeft: sample.scrollLeft,
  };

  if (
    deltaTop < FLOATING_TOOLBAR_SCROLL_DISMISS_THRESHOLD &&
    deltaLeft < FLOATING_TOOLBAR_SCROLL_DISMISS_THRESHOLD
  ) {
    return { nextState, dismiss: false };
  }

  return { nextState, dismiss: true };
}